// ---------------------------------------------------------------------------
// Customer-facing measurement and route formatting helpers (#289).
//
// Dimensions and weights can arrive from the API as decimal strings (Rails
// serializes BigDecimal values as strings, e.g. "430.0"), which render a
// needless trailing ".0" in customer messages. These helpers strip that
// dangling decimal for whole numbers while preserving any non-integer
// precision the value carries (e.g. "2.5" stays "2.5"). The route arrow is
// defined once so every customer surface renders the same symbol used by the
// quote emails and PDFs.
// ---------------------------------------------------------------------------

// Single source of truth for the route separator rendered on customer surfaces
// (dashboard lists, detail cards). Must match the arrow used in the emails and
// PDFs so the symbol is consistent everywhere.
export const ROUTE_ARROW = '→';

// Format a single dimension or weight value without a trailing ".0" for whole
// numbers, while keeping non-integer precision intact. Mirrors the price
// helper's approach in `lib/currency.ts` so numeric shapes stay consistent.
export const formatMeasurement = (
  raw: string | number | null | undefined,
): string => {
  if (raw === null || raw === undefined) return '';
  const str = String(raw).trim();
  if (str === '') return '';
  // "430.0" / "430.00" -> "430"; "2.5" / "2.50" are left untouched.
  return str.replace(/\.0+$/, '').replace(/\.$/, '');
};

// ---------------------------------------------------------------------------
// Quote-request date formatting (#287).
//
// A request stores its date at one of three precisions: a fixed date keeps the
// exact day, a flexible date is only a year and month ("YYYY-MM"), and a
// "none"/"no_date" request has no date. Dates render in the active UI language.
//
// A "YYYY-MM-DD" value is parsed as a local calendar date — never with
// `new Date("YYYY-MM-DD")`, which is interpreted as UTC and shifts the day back
// in western time zones (2026-06-15 -> June 14 in America/Los_Angeles). A
// "YYYY-MM" value is anchored to the first of its month only so the month and
// year can be formatted; the day is never displayed.
// ---------------------------------------------------------------------------

export type QuoteRequestDateType = 'fixed' | 'flexible' | 'none' | 'no_date';

// The Rails enum is `no_date: "none"`, and the enum reader returns the key
// (`"no_date"`), so the API emits `"no_date"` for an unspecified request even
// though the form submits `"none"`. Both spellings mean "no date" (#287).
export const isUnspecifiedDateType = (
  dateType: QuoteRequestDateType | undefined,
): boolean => dateType === 'none' || dateType === 'no_date';

// The app's fallback language (see src/i18n.ts). Used only when a caller has no
// active language, e.g. a unit test rendering outside an i18n provider.
const DEFAULT_LOCALE = 'en-US';

const CALENDAR_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_ONLY_PATTERN = /^(\d{4})-(\d{2})(?:-\d{2})?$/;

// Parse a "YYYY-MM-DD" value as a local calendar date so the day never shifts
// with the timezone. Any other value (e.g. a full ISO timestamp, which carries
// a real instant) falls back to `Date`; invalid input returns null.
const parseCalendarDate = (value: string): Date | null => {
  const dateOnly = CALENDAR_DATE_PATTERN.exec(value.trim());
  if (dateOnly) {
    const year = Number(dateOnly[1]);
    const month = Number(dateOnly[2]);
    const day = Number(dateOnly[3]);
    const date = new Date(year, month - 1, day);
    const isExactCalendarDate =
      date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
    return isExactCalendarDate ? date : null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Parse a flexible request date into a local Date anchored to the first of its
// month. Matches the backend's `format_month_year` regex, so a "YYYY-MM" value
// or a fuller "YYYY-MM-DD" value both collapse to month precision and the day
// is never rendered. Returns null for anything that is not month-precision.
const parseRequestMonth = (value: string | null | undefined): Date | null => {
  if (!value) return null;

  const match = MONTH_ONLY_PATTERN.exec(value.trim());
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;

  const date = new Date(year, month - 1, 1);
  return Number.isNaN(date.getTime()) ? null : date;
};

export interface QuoteRequestDateFormatOptions {
  /** Active UI language (BCP-47); defaults to the app fallback. */
  language?: string;
  /** `long` for detail views, `compact` for list rows. */
  style?: 'long' | 'compact';
}

/**
 * Render a quote request's origin/destination date faithfully to its
 * precision, in the active UI language (#287):
 *   - fixed    -> the exact day (e.g. "June 15, 2026")
 *   - flexible -> the month and year only, never a fabricated day
 *                 (e.g. "março de 2026" / "March 2026")
 *   - none / no_date / missing / invalid -> null, so the caller can render a
 *                 localized fallback (e.g. "No specific date").
 */
export const formatQuoteRequestDate = (
  dateType: QuoteRequestDateType | undefined,
  value: string | null | undefined,
  options: QuoteRequestDateFormatOptions = {},
): string | null => {
  if (!value) return null;

  const locale = options.language || DEFAULT_LOCALE;
  const style = options.style ?? 'long';

  if (dateType === 'fixed') {
    const date = parseCalendarDate(value);
    if (!date) return null;
    return new Intl.DateTimeFormat(
      locale,
      style === 'long' ? { dateStyle: 'long' } : { month: 'short', day: 'numeric' },
    ).format(date);
  }

  if (dateType === 'flexible') {
    const date = parseRequestMonth(value);
    if (!date) return null;
    return new Intl.DateTimeFormat(locale, {
      month: style === 'long' ? 'long' : 'short',
      year: 'numeric',
    }).format(date);
  }

  return null;
};
